import React, { useState } from 'react';
import { WaveConfig } from '../WaveBackground/config';
import WaveBackground from '../WaveBackground';
import WaveBackgroundControlPanel from '../WaveBackgroundControlPanel';
import { useFeatureFlags } from '../../../utils/featureFlags';

/**
 * CyclicWaveBackground Component
 *
 * A composable animation adapter component that provides cyclic transitions between two wave states.
 * This component wraps the base WaveBackground component and handles the animation logic,
 * allowing for smooth transitions between different wave configurations.
 *
 * Features:
 * - Smooth interpolation between wave configurations
 * - Configurable cycle timing and behavior
 * - Cubic easing for natural transitions
 * - Automatic animation cleanup
 *
 * Animation Cycle:
 * 1. Hold at start configuration (startWaitTime)
 * 2. Smoothly interpolate to end configuration (transitionTime)
 * 3. Hold at end configuration (endWaitTime)
 * 4. Smoothly interpolate back to start configuration (transitionTime)
 * 5. Repeat
 *
 * @example
 * ```tsx
 * <CyclicWaveBackground
 *   startConfig={startWaveConfig}
 *   endConfig={endWaveConfig}
 *   cycleConfig={{
 *     cycleDuration: 30000,    // 30 seconds per cycle
 *     startWaitTime: 0.4,      // 40% of cycle
 *     endWaitTime: 0.3,        // 30% of cycle
 *     transitionTime: 0.1      // 10% of cycle for each transition
 *   }}
 * />
 * ```
 */

interface CyclicWaveBackgroundProps {
  /** Initial wave configuration */
  startConfig: WaveConfig;
  /** Target wave configuration to transition to */
  endConfig: WaveConfig;
  /** Show wave control panel */
  panel?: boolean;
  /** Initial collapsed state of control panel */
  initialCollapsed?: boolean;
  /** Timing configuration for the animation cycle */
  cycleConfig?: {
    /** Total duration of one cycle in milliseconds */
    cycleDuration?: number;
    /** Percentage of cycle to wait at start config (0-1) */
    startWaitTime?: number;
    /** Percentage of cycle to wait at end config (0-1) */
    endWaitTime?: number;
    /** Percentage of cycle for each transition (0-1) */
    transitionTime?: number;
  };
}

// Removed unused variables to fix linting errors

const CyclicWaveBackground: React.FC<CyclicWaveBackgroundProps> = ({
  startConfig,
  endConfig,
  cycleConfig = {},
  panel,
  initialCollapsed
}) => {
  const { waveController } = useFeatureFlags();

  // Only used for the control panel mode
  const [currentConfig, setCurrentConfig] = useState<WaveConfig>(startConfig);

  // Control panel mode: keep as before
  if (waveController) {
    return (
      <WaveBackgroundControlPanel
        config={currentConfig}
        onConfigChange={setCurrentConfig}
      />
    );
  }

  // Animated mode: pass configs and cycleConfig, let WaveBackground handle animation
  return (
    <WaveBackground
      startConfig={startConfig}
      endConfig={endConfig}
      cycleConfig={cycleConfig}
    />
  );
};

export default CyclicWaveBackground;